import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Link
} from '@mui/material';
import {
  Email,
  CheckCircle,
  Refresh,
  Psychology,
  ArrowBack
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const EmailVerification = ({ user, devEmailHint: initialDevEmailHint, onSuccess, onBackToLogin }) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [devEmailHint, setDevEmailHint] = useState(initialDevEmailHint || null);

  const { verifyEmailOTP, resendEmailOTP } = useAuth();

  // Countdown timer for resend button
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setOtp(value);
      setError('');
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await verifyEmailOTP(user.email, otp);

      if (result.success) {
        setSuccess('Email verified successfully!');
        setTimeout(() => {
          onSuccess && onSuccess(result.user);
        }, 1500);
      } else {
        setError(result.error || 'Verification failed. Please check your code and try again.');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('Verification failed. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    setError('');
    setSuccess('');

    try {
      const result = await resendEmailOTP(user.email);

      if (result.success) {
        if (result.devEmailHint) {
          setDevEmailHint(result.devEmailHint);
        }
        setSuccess(result.message || 'Verification code sent to your email');
        setCountdown(60); // 60 second cooldown
        setOtp(''); // Clear current OTP
      } else {
        setError(result.error || 'Failed to resend verification code. Please try again.');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError('Failed to resend verification code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%', boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Psychology sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Verify Your Email
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              We've sent a 6-digit verification code to:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {user.email}
            </Typography>
          </Box>

          {/* Local dev: OTP when email is mocked */}
          {devEmailHint?.devOtp && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Local development — verification code
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {devEmailHint.message || 'Email is not sent when MOCK_EMAILS=true. Use this code:'}
              </Typography>
              <Typography
                variant="h5"
                component="div"
                sx={{ fontFamily: 'monospace', letterSpacing: '0.35rem', fontWeight: 700 }}
              >
                {devEmailHint.devOtp}
              </Typography>
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
              {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* OTP Form */}
          <Box component="form" onSubmit={handleVerifyOtp}>
            <TextField
              fullWidth
              name="otp"
              label="Verification Code"
              value={otp}
              onChange={handleOtpChange}
              placeholder="Enter 6-digit code"
              required
              sx={{ mb: 3 }}
              inputProps={{
                maxLength: 6,
                style: { 
                  textAlign: 'center', 
                  fontSize: '1.5rem', 
                  letterSpacing: '0.5rem',
                  fontWeight: 'bold'
                }
              }}
              InputProps={{
                startAdornment: (
                  <Email color="action" sx={{ mr: 1 }} />
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || otp.length !== 6}
              sx={{
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0a3d8f 30%, #002171 90%)'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Verify Email'
              )}
            </Button>

            {/* Resend Section */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Didn't receive the code?
              </Typography>
              
              {countdown > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Resend available in {countdown} seconds
                </Typography>
              ) : (
                <Button
                  variant="text"
                  onClick={handleResendOtp}
                  disabled={resending}
                  startIcon={resending ? <CircularProgress size={16} /> : <Refresh />}
                  sx={{ textTransform: 'none' }}
                >
                  {resending ? 'Sending...' : 'Resend Code'}
                </Button>
              )}
            </Box>

            {/* Back to Login */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="text"
                onClick={onBackToLogin}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Back to Login
              </Button>
            </Box>
          </Box>

          {/* Help Text */}
          <Box sx={{ mt: 4, p: 2, bgcolor: 'info.50', borderRadius: 1, border: 1, borderColor: 'info.200' }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              <strong>Having trouble?</strong><br/>
              • Check your spam/junk folder<br/>
              • Make sure you entered the correct email address<br/>
              • The code expires in 10 minutes<br/>
              • Contact support if you continue having issues
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmailVerification;